/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { IManageModePanelFrameFacade } from '@hcworkspace/sysque/toolbelt/data-access';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { InitializeSystemInfo, sendPopupRoutingAction } from '../toolbelt.actions';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { selectSystemInfo } from '../tool-belt.selector';
import { TooltipItem } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tooltip-item';
import { TooltipItemInfo } from '../../../../../utils/src/lib/common-helper/tooltip-item-info/tooltip-item-info';
import { GlobalConstants } from '../../constants/global-constants';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';

@Injectable({ providedIn: 'root' })
export class ManageModePanelFrameFacade implements IManageModePanelFrameFacade { 
  constructor(
    private store: Store,
    public localStorageService: LocalStorageService,
    private backendService: BackendService,
    private stateManager: SystemStateManager
  ) {} 

  public logicToExecuteForHangerCopyCategory(items: PastedItemsInfo[], isSetUp: boolean): void {
     this.backendService.messageHandler.sendRequestAsync('LogicToExecuteForHangerCopyCategory', [items, isSetUp]);
  }

  public setManageModeDestinationState(state: SysqueElectricalState): void {
     this.stateManager.setManageModeDestinationState(state);
  }

  public openHangerSpecification(): void {
   let sysquePopupConfig = new SysquePopupConfig();
    createDialog(sysquePopupConfig, GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG);
    this.store.dispatch(
      sendPopupRoutingAction({
        route: GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG,
        width: sysquePopupConfig.width,
        height: sysquePopupConfig.height,
      })
    );
  }

  public logicToExecuteForCopyCategory(isReplace: boolean): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.LogicToExecuteForCopyCategory, [isReplace]);
  }

  public checkForDuplicateItemsInDestForCopyCategory(items: PastedItemsInfo[]): Observable<boolean> {
    return from<Promise<boolean>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CheckForDuplicateItemsInDestForCopyCategory, [
        items,
      ])
    );
  }

  public getLocalStorageForDuplicateKeyStatus(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.CanExecutePasteLogic);
  }

  public getLocalStorageForHangerSetUpStatus(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.CopyCategoryIsHangerSetUp);
  }

  public executePasteItemsAction(replaceItems: PastedItemsInfo[]): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.LogicToExecuteForPasteItems, [replaceItems]);
  }

  public checkForDuplicateItemsInDestination(items: PastedItemsInfo[]): Promise<PastedItemsInfo[]> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CheckForDuplicateItems, [items]);
  }

  public selectSystemsForDestination(): Observable<SystemInfo[]> {
    return this.stateManager.selectSystemsForDestination();
  }

  public selectSystemsForSource(): Observable<SystemInfo[]> {
    return this.stateManager.selectSystemsForSource();
  }

  public selectFittingForDestination(): Observable<Item[]> {
    return this.stateManager.selectFittingForDestination();
  }

  public selectFittingForSource(): Observable<Item[]> {
    return this.stateManager.selectFittingForSource();
  }

  public selectCategoriesForSourceSystem(): Observable<TabDescription[]> {
    return this.stateManager.selectCategoriesForSource();
  }

  public selectCategoriesForDestinationSystem(): Observable<TabDescription[]> {
    return this.stateManager.selectCategoriesForDestination();
  }

  public getManageModeSourceState(): SysqueElectricalState {
    return this.stateManager.getManageModeSourceState();
  }

  public getManageModeDestinationState(): SysqueElectricalState {
    return this.stateManager.getManageModeDestinationState();
  }

  public getDockedState(): SysqueElectricalState {
    return this.stateManager.getDockedModeState();
  }

  public getAddFittingState(): SysqueElectricalState {
    return this.stateManager.getAddFittingModeState();
  }

  public notifyGetFittingsListAction(systemName: string, categoryName: string): Observable<Item[]> {
    return from<Promise<Item[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [
        systemName,
        categoryName,
        false
      ])
    );
  }

  public notifyGetCategoryListAction(systemName: string): Observable<TabDescription[]> {
    if (!systemName) {
      return;
    }
    return from<Promise<TabDescription[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetCategories, [systemName])
    );
  }

  public updateLocalStorage(key: string, value: any) {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public sendPopupRoutingAction(route: string, width: number, height: number) {
    this.store.dispatch(sendPopupRoutingAction({ route, width: width, height: height }));
  }

  public saveChanges(actionName: string): void {
    this.stateManager.saveChanges(actionName);
  }

  public resetFittingModeState(): void {
    this.stateManager.resetFittingMode();
  }

  public resetManageState(): void {
    this.stateManager.resetManageMode();
  }

  public resetDockedState(): void {
    this.stateManager.getDockedModeState();
  }

  public updateSystemList(): void {
    const manageModeSourceState = this.getManageModeSourceState();
    const manageModeDestinationState = this.getManageModeDestinationState();
    this.store.dispatch(InitializeSystemInfo());

    this.store.select(selectSystemInfo).subscribe((data) => {
      manageModeSourceState.systemList = data;
      manageModeDestinationState.systemList = data;
    });
    this.stateManager.setManageModeSourceState(manageModeSourceState);
    this.stateManager.setManageModeDestinationState(manageModeDestinationState);
  }

  public getFittings(systemName: string, categoryName: string): Observable<Item[]> {
    return from<Promise<Item[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [
        systemName,
        categoryName,
        false
      ])
    );
  }

  public selectSystems(): Observable<SystemInfo[]> {
    const state = this.stateManager.getAddFittingModeState();
    return of(state.systemList);
  }

  public getTooltipItemInfo(item: Item, category: string): TooltipItem[] {
    return new TooltipItemInfo().getTooltipItemInfo(item, category);
  }

  public getLocalStorageUpdate(): Observable<string> {
    return this.localStorageService.localStorageUpdate$;
  }
}